import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { rng, seedOf } from './noise.js';

/**
 * The rest of the property: what stands round the game and is never reached.
 *
 * Read from `plan.scenery` (see `data/level.js`), and nothing else. The hotel's
 * main building, a Y of guest wings north of the building the game happens in;
 * the porte-cochère and the drive that loops in to it from the west; the guest
 * parking, west and south-west, with cars in it; the residences round a small
 * lake down the road; the Caytons' fairways past its boundary; and the public
 * road past the gatehouse's boom.
 *
 * None of it is solid and none of it is inside a room. It is drawn on the
 * desert floor, past the walls, where the vegetation's scatter keeps clear of
 * it, and nothing in the game can tell it is there.
 */

const FT = 0.3048;
const m = (feet) => feet * FT;

/** Every attribute but position, normal and uv dropped, so geometries merge. */
function clean(g) {
  const out = g.index ? g.toNonIndexed() : g;
  for (const name of Object.keys(out.attributes)) if (!['position', 'normal', 'uv'].includes(name)) out.deleteAttribute(name);
  return out;
}

/** A box on the ground, centred on (x, z) in metres, turned by `yaw` radians. */
function slab(x, z, w, d, h, yaw = 0, y = 0) {
  return new THREE.BoxGeometry(w, h, d).translate(0, y + h / 2, 0).rotateY(yaw).translate(x, 0, z);
}

/** World-metre texture coordinates on a flat piece, so a tile is the size it says. */
function worldUV(geometry) {
  const p = geometry.attributes.position;
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i += 1) {
    uv[i * 2] = p.getX(i);
    uv[i * 2 + 1] = p.getZ(i);
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geometry;
}

/** A flat shape on the ground: a rect or an ellipse or a circle, in feet. */
function ground(shape, y) {
  let g;
  if (shape.rect) {
    const [x0, z0, x1, z1] = shape.rect;
    g = new THREE.PlaneGeometry(m(Math.abs(x1 - x0)), m(Math.abs(z1 - z0))).rotateX(-Math.PI / 2).translate(m((x0 + x1) / 2), y, m((z0 + z1) / 2));
  } else if (shape.circle) {
    const [x, z, r] = shape.circle;
    g = new THREE.CircleGeometry(m(r), 40).rotateX(-Math.PI / 2).translate(m(x), y, m(z));
  } else if (shape.ellipse) {
    const [x, z, rx, rz] = shape.ellipse;
    g = new THREE.CircleGeometry(1, 40).rotateX(-Math.PI / 2).scale(m(rx), 1, m(rz)).translate(m(x), y, m(z));
  } else {
    return null;
  }
  return worldUV(g);
}

/** The footprint of a scenery shape, in metres, for keeping plants off it. */
export function footprints(scenery) {
  const out = [];
  const pad = 3;
  for (const mass of scenery?.masses ?? []) {
    const r = Math.hypot(mass.size[0], mass.size[1]) / 2;
    out.push({ minX: m(mass.at[0] - r) - pad, maxX: m(mass.at[0] + r) + pad, minZ: m(mass.at[1] - r) - pad, maxZ: m(mass.at[1] + r) + pad });
  }
  for (const shape of [...(scenery?.paved ?? []), ...(scenery?.water ?? []), ...(scenery?.fairways ?? [])]) {
    if (shape.rect) {
      const [x0, z0, x1, z1] = shape.rect;
      out.push({ minX: m(Math.min(x0, x1)) - pad, maxX: m(Math.max(x0, x1)) + pad, minZ: m(Math.min(z0, z1)) - pad, maxZ: m(Math.max(z0, z1)) + pad });
    } else {
      const [x, z, rx, rz = rx] = shape.circle ?? shape.ellipse;
      out.push({ minX: m(x - rx) - pad, maxX: m(x + rx) + pad, minZ: m(z - rz) - pad, maxZ: m(z + rz) + pad });
    }
  }
  return out;
}

export function createSite({ plan, bank = null }) {
  const group = new THREE.Group();
  group.name = 'look-site';
  const disposables = [];
  const scenery = plan.scenery ?? {};
  const material = (options) => {
    const made = new THREE.MeshStandardMaterial({ roughness: 0.9, ...options });
    disposables.push(made);
    return made;
  };
  const surface = (name, tint) => {
    const s = bank?.surface?.(name);
    return material(s ? { map: s.color, roughness: s.roughness, color: tint ?? 0xffffff } : { color: tint ?? 0x888888 });
  };
  const add = (list, made, { shadows = true } = {}) => {
    const parts = list.filter(Boolean).map(clean);
    if (!parts.length) return null;
    const geometry = mergeGeometries(parts);
    disposables.push(geometry);
    const mesh = new THREE.Mesh(geometry, made);
    mesh.castShadow = shadows;
    mesh.receiveShadow = true;
    group.add(mesh);
    return mesh;
  };

  // --- the buildings ------------------------------------------------------------
  const stucco = material({ color: 0xcdb592 });
  const roof = material({ color: 0x8e8478 });
  const glass = material({ color: 0x3a4552, roughness: 0.25, metalness: 0.4 });
  const trim = material({ color: 0xa88a64 });
  const walls = [];
  const roofs = [];
  const windows = [];
  const trims = [];
  for (const mass of scenery.masses ?? []) {
    const x = m(mass.at[0]);
    const z = m(mass.at[1]);
    const w = m(mass.size[0]);
    const d = m(mass.size[1]);
    const h = m(mass.height);
    const yaw = ((mass.yaw ?? 0) * Math.PI) / 180;
    if (mass.kind === 'canopy') {
      // Four columns and a deep flat roof over the end of the drive.
      for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        const c = new THREE.Vector3(sx * (w / 2 - 0.6), 0, sz * (d / 2 - 0.6)).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
        trims.push(slab(x + c.x, z + c.z, 0.9, 0.9, h - 1.4, yaw));
      }
      roofs.push(slab(x, z, w, d, 1.4, yaw, h - 1.4));
      continue;
    }
    if (mass.kind === 'bridge') {
      // A closed bridge on two piers, from `base` up: the drive runs under it.
      const base = m(mass.base ?? 14);
      walls.push(slab(x, z, w, d, h - base, yaw, base));
      trims.push(slab(x - w / 2 + 0.6, z, 1.2, d, base, yaw), slab(x + w / 2 - 0.6, z, 1.2, d, base, yaw));
      windows.push(slab(x, z, w * 0.9, d + 0.06, (h - base) * 0.4, yaw, base + (h - base) * 0.3));
      continue;
    }
    walls.push(slab(x, z, w, d, h, yaw));
    // A parapet, and the roof inside it.
    roofs.push(slab(x, z, w - 0.8, d - 0.8, 0.2, yaw, h));
    trims.push(slab(x, z, w + 0.2, d + 0.2, 0.5, yaw, h - 0.5));
    // A band of glass on every face, a storey at a time, with the wall between.
    const storey = mass.kind === 'villa' ? m(16) : m(10.5);
    for (let y = storey * 0.35; y + storey * 0.45 < h - 0.6; y += storey) {
      windows.push(slab(x, z, w + 0.06, d * 0.86, storey * 0.42, yaw, y));
      windows.push(slab(x, z, w * 0.86, d + 0.06, storey * 0.42, yaw, y));
    }
  }
  add(walls, stucco);
  add(roofs, roof);
  add(windows, glass, { shadows: false });
  add(trims, trim);

  // --- the ground: paving, stalls, cars, the lake, the fairways ----------------------
  const asphalt = surface('asphalt');
  const paved = [];
  const lines = [];
  const cars = [];
  const next = rng(seedOf('the guest parking'));
  for (const shape of scenery.paved ?? []) {
    paved.push(ground(shape, 0.012));
    if (!shape.stalls || !shape.rect) continue;
    // Rows of stalls across the lot, nine feet wide and eighteen deep, back to
    // back either side of a twenty-four foot aisle.
    const [x0, z0, x1, z1] = shape.rect;
    const minX = Math.min(x0, x1) + 6;
    const maxX = Math.max(x0, x1) - 6;
    for (let rowZ = Math.min(z0, z1) + 4; rowZ + 36 <= Math.max(z0, z1); rowZ += 60) {
      for (const [from, depth] of [[rowZ, 18], [rowZ + 18, 18]]) {
        for (let sx = minX; sx <= maxX + 0.01; sx += 9) lines.push(slab(m(sx), m(from + depth / 2), 0.1, m(depth), 0.01, 0, 0.014));
        for (let sx = minX + 4.5; sx < maxX; sx += 9) {
          if (next() > 0.45) continue;
          const len = 14 + next() * 3;
          const car = m(from + depth / 2);
          cars.push({ x: m(sx), z: car, len: m(len), tone: Math.floor(next() * 5) });
        }
      }
    }
  }
  add(paved, asphalt, { shadows: false });
  add(lines, material({ color: 0xe8e6de }), { shadows: false });
  const tones = [0xd8d8d4, 0x2a2c30, 0x8a8e94, 0x6a1e24, 0x2c3a52];
  tones.forEach((tone, index) => {
    const bodies = [];
    for (const car of cars.filter((c) => c.tone === index)) {
      bodies.push(slab(car.x, car.z, m(6), car.len, m(2.6), 0, 0.2));
      bodies.push(slab(car.x, car.z + car.len * 0.05, m(5.4), car.len * 0.5, m(1.6), 0, 0.2 + m(2.6)));
    }
    add(bodies, material({ color: tone, roughness: 0.4, metalness: 0.5 }));
  });

  const water = material({ color: 0x3f7f92, roughness: 0.15, metalness: 0.2 });
  const shore = material({ color: 0xa08a6a });
  for (const shape of scenery.water ?? []) {
    const [x, z, rx, rz] = shape.ellipse;
    add([ground({ ellipse: [x, z, rx + 6, rz + 6] }, 0.01)], shore, { shadows: false });
    add([ground(shape, 0.03)], water, { shadows: false });
  }
  add((scenery.fairways ?? []).map((shape) => ground(shape, 0.008)), surface('grass', 0xd8e8c8), { shadows: false });

  // --- the pool's water --------------------------------------------------------------
  /*
   * A room whose floor is water is a hole with a pool floor at the bottom of
   * it, and the desert's ground plane runs under the whole site a few
   * centimetres below the deck: from above it hides the pool floor. The
   * water's surface is drawn over it, just under the deck, see-through. Not
   * solid, like everything here: you still go in.
   */
  const surfaceOf = material({ color: 0x3aa0bc, roughness: 0.08, metalness: 0.1, transparent: true, opacity: 0.82 });
  for (const room of (plan.rooms ?? []).filter((r) => r.floor === 'water')) {
    add([ground({ rect: room.rect }, -0.02)], surfaceOf, { shadows: false });
  }

  // --- the buildings the level stands as solid boxes ----------------------------------
  /*
   * The Saltbush, the Caytons' clubhouse and the gatehouse are props: solid,
   * the size they are, and a plain box to the greybox. Here they get what a
   * low desert building has, on the outside of their own boxes: a band of
   * glass along every face, a deep roof edge, and a stone base course.
   */
  const buildingIds = ['saltbush', 'clubhouse', 'gatehouse'];
  const bands = [];
  const edges = [];
  const bases = [];
  for (const prop of (plan.props ?? []).filter((p) => buildingIds.includes(p.id))) {
    const [x0, z0, x1, z1] = prop.rect;
    const cx = m((x0 + x1) / 2);
    const cz = m((z0 + z1) / 2);
    const w = m(Math.abs(x1 - x0));
    const d = m(Math.abs(z1 - z0));
    const h = m(prop.height);
    bands.push(slab(cx, cz, w + 0.06, d * 0.8, h * 0.34, 0, h * 0.3));
    bands.push(slab(cx, cz, w * 0.8, d + 0.06, h * 0.34, 0, h * 0.3));
    edges.push(slab(cx, cz, w + 0.9, d + 0.9, 0.45, 0, h - 0.1));
    bases.push(slab(cx, cz, w + 0.08, d + 0.08, 0.5, 0, 0));
  }
  add(bands, glass, { shadows: false });
  add(edges, trim);
  add(bases, material({ color: 0x7a6a58 }));

  return {
    object3D: group,
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}
